import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import Commongrid from "../../common/Commongrid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const Card = ({ title, value, color }) => {
  return (
    <div className="relative group w-full max-w-xs">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>

      <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition duration-300">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-500 tracking-wide">
            {title}
          </h3>
        </div>

        <div className="mt-4">
          <p className={`text-4xl font-extrabold tracking-tight ${color}`}>
            {value ?? 0}
          </p>
        </div>
      </div>
    </div>
  );
};

const Viewmysales = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("inquiry");
  const [copied, setCopied] = useState(false);

  const fleetBookingStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.fleetBookingStatus;

  const getSalesDashboard = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${apiUrl}/api/salesagentdashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
      });

      const data = await response.json();

      if (data?.statusCode === "0100") {
        setDashboardData(data.dataValues);
      }
    } catch (error) {
      console.log("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSalesDashboard();
  }, []);

  const columns = [
    {
      field: "inquiryCode",
      headerName: "Inquiry Code",
      minWidth: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center", // 👈 center vertically
            justifyContent: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <span>{params.value || "-"}</span>

          {params.value && (
            <ContentCopyIcon
              sx={{
                fontSize: 18,
                cursor: "pointer",
                color: copied === params.value ? "green" : "grey",
                transition: "color 0.2s",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(params.value);
                setCopied(params.value);
                setTimeout(() => setCopied(false), 1500);
              }}
            />
          )}
        </Box>
      ),
    },
    { field: "bookingStatus", headerName: "Booking Status", minWidth: 140 },
    { field: "customerName", headerName: "Customer Name", minWidth: 160 },
    { field: "customerMobile", headerName: "Customer Mobile", minWidth: 140 },
    { field: "customerEmail", headerName: "Customer Email", minWidth: 200 },
    { field: "bookFrom", headerName: "Pickup Date", minWidth: 180 },
    { field: "bookTo", headerName: "Drop Date", minWidth: 180 },
    { field: "createdAt", headerName: "Created Date", minWidth: 180 },
    {
      field: "inquiryAssignedOn",
      headerName: "Inquiry Assigned On",
      minWidth: 180,
    },
    { field: "costToCustomer", headerName: "Amount", minWidth: 130 },
    { field: "weekdayRent", headerName: "Weekday Rent", minWidth: 150 },
    { field: "weekendRent", headerName: "Weekend Rent", minWidth: 150 },
    { field: "discountAmount", headerName: "Discount", minWidth: 130 },
    {
      field: "baseRentWithDiscount",
      headerName: "Base Rent With Discount",
      minWidth: 130,
    },
    {
      field: "baseRentWithoutDiscount",
      headerName: "Base Rent Without Discount",
      minWidth: 130,
    },
    { field: "source", headerName: "Source", minWidth: 130 },
    { field: "originalSource", headerName: "Original Source", minWidth: 130 },
  ];

  let rowsData = [];

  if (activeTab === "inquiry") {
    rowsData = dashboardData?.inquiryList || [];
  } else if (activeTab === "booking") {
    rowsData = dashboardData?.myBookings || [];
  } else if (activeTab === "monthly") {
    rowsData = dashboardData?.monthlyBookingList || [];
  }

  const rows =
    rowsData?.map((item, index) => ({
      id: item?.id || index,
      inquiryCode: item?.inquiryCode || "-",
      bookingStatus: fleetBookingStatus?.[item?.bookingStatus] || "-",
      customerName: item?.customerName || "-",
      customerMobile: item?.customerMobile || "-",
      customerEmail: item?.customerEmail || "-",
      bookFrom: item?.bookFrom ? new Date(item.bookFrom).toLocaleString() : "-",
      bookTo: item?.bookTo ? new Date(item.bookTo).toLocaleString() : "-",
      createdAt: item?.createdAt
        ? new Date(item.createdAt).toLocaleString()
        : "-",
      inquiryAssignedOn: item?.inquiryAssignedOn,
      costToCustomer: item?.costToCustomer || 0,
      weekdayRent: item?.weekdayRent || 0,
      weekendRent: item?.weekendRent || 0,
      discountAmount: item?.discountAmount || 0,
      baseRentWithDiscount: item?.baseRentWithDiscount || 0,
      baseRentWithoutDiscount: item?.baseRentWithoutDiscount || 0,
      source: item?.source || "-",
      originalSource: item?.originalSource || "-",
    })) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">My Sales Dashboard</h1>
        <Button variant="contained" onClick={getSalesDashboard}>
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card
          title="Total Inquiry Assigned"
          value={dashboardData?.totalInqiryCount}
          color="text-orange-500"
        />
        <Card
          title="Today Revenue"
          value={Number(dashboardData?.myGenerateRevenueToday || 0).toFixed(2)}
          color="text-green-500"
        />

        <Card
          title="Monthly Revenue"
          value={Number(dashboardData?.myGenerateRevenueMonthly || 0).toFixed(
            2,
          )}
          color="text-green-800"
        />
        <Card
          title="Today's Target"
          value={Number(dashboardData?.tillTodayDeficit).toFixed(2)}
          color="text-blue-500"
        />
        <Card
          title="Monthly Target"
          value={dashboardData?.myTarget?.monthlyAgentTarget}
          color="text-blue-800"
        />
        {/* <Card
          title="Today's Deficit"
          value={"-" + Number(dashboardData?.tillTodayDeficit || 0).toFixed(2)}
          color="text-red-500"
        /> */}
        <Card
          title="Monthly Deficit"
          value={"-" + Number(dashboardData?.monthlyDeficit || 0).toFixed(2)}
          color="text-red-800"
        />
        <Card
          title="Target Till Date"
          value={dashboardData?.tillTodayTarget}
          color="text-orange-500"
        />
        <Card
          title="Monthly Revenue Till Date"
          value={Number(dashboardData?.tillTodayRevenueGenerated || 0).toFixed(2)}
          color="text-green-500"
        />

        {/* <Card
          title="MTD Deficit"
          value={"-" + Number(dashboardData?.tillTodayDeficit || 0).toFixed(
            2,
          )}
          color="text-green-800"
        /> */}
      </div>

      <div className="flex gap-6 border-b pb-2 mt-8">
        <button
          className={
            activeTab === "inquiry"
              ? "font-bold border-b-2 border-blue-500"
              : ""
          }
          onClick={() => setActiveTab("inquiry")}
        >
          My Inquiries
        </button>

        <button
          className={
            activeTab === "booking"
              ? "font-bold border-b-2 border-blue-500"
              : ""
          }
          onClick={() => setActiveTab("booking")}
        >
          My Today Bookings
        </button>

        <button
          className={
            activeTab === "monthly"
              ? "font-bold border-b-2 border-blue-500"
              : ""
          }
          onClick={() => setActiveTab("monthly")}
        >
          My Monthly Bookings
        </button>
      </div>

      <Commongrid
        rows={rows}
        columns={columns}
        totalCount={rows.length}
        handleViewClick={(params) => {
          const navigate = `/viewallbookings`;
          window.open(navigate, "_blank");
        }}
        loading={loading}
        disableColumnMenu={true}
      />
    </div>
  );
};

export default Viewmysales;
